import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, ExternalLink, Server, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAbmConnections } from "@/hooks/useAbmConnections";
import { formatDistanceToNow } from "date-fns";

interface AbmConfigStepProps {
  selectedAbmConnectionId: string;
  onSelectAbmConnection: (id: string) => void;
  customerId?: string;
}

export const AbmConfigStep = ({ 
  selectedAbmConnectionId, 
  onSelectAbmConnection,
  customerId 
}: AbmConfigStepProps) => {
  const { connections, loading } = useAbmConnections(customerId);
  const activeConnections = connections.filter(c => c.is_active);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">ABM Configuration</h2>
          <p className="text-muted-foreground">Loading ABM connections...</p>
        </div>
      </div>
    );
  }

  if (activeConnections.length === 0) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">ABM Configuration</h2>
          <p className="text-muted-foreground">
            Select an Apple Business Manager account for this enrollment profile
          </p>
        </div>

        <Card disableHover className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader>
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
              <div>
                <CardTitle className="text-lg">No ABM Connections Found</CardTitle>
                <CardDescription className="mt-1">
                  {customerId 
                    ? "No Apple Business Manager connections found for this customer. Please configure one in Settings."
                    : "You need to configure an Apple Business Manager connection before creating enrollment profiles."}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/settings?tab=integrations">
                <Server className="h-4 w-4 mr-2" />
                Configure ABM in Settings
                <ExternalLink className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">ABM Configuration</h2>
        <p className="text-muted-foreground">
          Select an Apple Business Manager account for this enrollment profile
        </p>
      </div>

      <RadioGroup value={selectedAbmConnectionId} onValueChange={onSelectAbmConnection}>
        <div className="space-y-3">
          {activeConnections.map((connection) => (
            <Card 
              disableHover
              key={connection.id} 
              className={`cursor-pointer transition-all ${
                selectedAbmConnectionId === connection.id 
                  ? 'border-primary bg-primary/5' 
                  : 'hover:border-primary/50'
              }`}
              onClick={() => onSelectAbmConnection(connection.id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <RadioGroupItem value={connection.id} id={connection.id} />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <Label htmlFor={connection.id} className="text-base font-semibold cursor-pointer">
                          {connection.name}
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Organization ID: {connection.organization_id}
                        </p>
                      </div>
                      <Badge variant="outline" className="gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        Active
                      </Badge>
                    </div>
                    
                    {connection.last_sync_at && (
                      <p className="text-xs text-muted-foreground">
                        Last synced {formatDistanceToNow(new Date(connection.last_sync_at), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};
